// ** React Imports
import { useMemo, memo } from "react";

// ** Redux & Store & Actions
import { useSelector } from "react-redux";

// ** Third Party Components
import { useTranslation } from "react-i18next";
import loadable from "@loadable/component";

// ** LocationChips Components
const LocationChipsAllWorldLevel = loadable(() =>
	import("./components/LocationChipsAllWorldLevel")
);
const LocationChipsCountryLevel = loadable(() =>
	import("./components/LocationChipsCountryLevel")
);
const LocationChipsCountryWithoutRegionsLevel = loadable(() =>
	import("./components/LocationChipsCountryWithoutRegionsLevel")
);
const LocationChipsRegionLevel = loadable(() =>
	import("./components/LocationChipsRegionLevel")
);
const LocationChipsCityLevel = loadable(() =>
	import("./components/LocationChipsCityLevel")
);

// #####################################################

const LocationChips = ({
	locationPath,
	queryAsString,
	countryUrl,
	regionUrl,
	cityUrl,
	withOtherCities,
}) => {
	const { t } = useTranslation(["countries", "pages", "regions"]);

	// ** Wybrany Kraj
	const locationSelectedCountry = useSelector(
		(state) => state.mainPageUsers.location_search.country
	);

	// ** Lista Wszystkich Krajów
	const allCountriesArray =
		useSelector((state) => state.mainPageUsers.location_search.countries) ||
		[];

	// ** Lista Wszystkich Krajów - Opracowana Lista
	const allCountries = useMemo(() => {
		return allCountriesArray.map((country) => {
			return {
				name: t(`countries:${country.id}`),
				url: country.url,
			};
		});
	}, [allCountriesArray]);

	// ** Wybrany Region
	const locationSelectedRegion = useSelector(
		(state) => state.mainPageUsers.location_search.region
	);

	// ** Lista Regionów w Wybranym Kraju
	const regionsByCountryArray =
		useSelector((state) => state.mainPageUsers.location_search.regions) ||
		[];

	// ** Lista Regionów w Wybranym Kraju - Opracowana Lista
	const regionsByCountry = useMemo(() => {
		if (countryUrl) {
			return [...regionsByCountryArray];
		} else {
			return [];
		}
	}, [regionsByCountryArray]);

	// ** Wybrane Miasto
	const locationSelectedCity = useSelector(
		(state) => state.mainPageUsers.location_search.city
	);

	// ** Lista Miast w Wybranym Regionie
	const citiesByRegionArray =
		useSelector((state) => state.mainPageUsers.location_search.cities) ||
		[];

	// ** Lista Miast w Wybranym Regionie
	const citiesByRegion = useMemo(() => {
		if (regionUrl) {
			return [...citiesByRegionArray];
		} else {
			return [];
		}
	}, [citiesByRegionArray]);

	const levelShow =
		[countryUrl, regionUrl, cityUrl].filter((element) => {
			return element !== null && element !== undefined;
		})?.length || 0;

	// #####################################################

	if (countryUrl && regionUrl && cityUrl) {
		return (
			<LocationChipsCityLevel
				country={locationSelectedCountry}
				region={locationSelectedRegion}
				city={locationSelectedCity}
				path={locationPath}
				queryString={queryAsString}
				withOtherCities={withOtherCities}
			/>
		);
	} else if (
		countryUrl &&
		regionUrl &&
		citiesByRegion?.length &&
		levelShow > 1
	) {
		return (
			<LocationChipsRegionLevel
				country={locationSelectedCountry}
				region={locationSelectedRegion}
				cities={citiesByRegion}
				path={locationPath}
				queryString={queryAsString}
			/>
		);
	} else if (countryUrl && regionsByCountry.length && levelShow === 1) {
		return (
			<LocationChipsCountryLevel
				country={locationSelectedCountry}
				regions={regionsByCountry}
				path={locationPath}
				queryString={queryAsString}
			/>
		);
	} else if (countryUrl && regionsByCountry.length === 0) {
		return (
			<LocationChipsCountryWithoutRegionsLevel
				country={locationSelectedCountry}
				path={locationPath}
				queryString={queryAsString}
			/>
		);
	} else if (!countryUrl && levelShow === 0) {
		return (
			<LocationChipsAllWorldLevel
				countries={allCountries}
				path={locationPath}
				queryString={queryAsString}
			/>
		);
	}

	return null;
};

export default memo(LocationChips);

// #####################################################
